import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Calculator, Briefcase, BarChart3, History, TrendingUp, Users, LogOut, CheckCircle, Settings, DollarSign, User, MapPin, Award, Map } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useDivision } from '@/context/DivisionContext';
import { Button } from '@/components/ui/button';

const Sidebar = () => {
  const { user, logout, isAdmin } = useAuth();
  const { division, switchDivision } = useDivision();
  const navigate = useNavigate();

  const handleDivisionChange = (e) => {
    switchDivision(e.target.value);
    // Refresh to dashboard to load new division data
    navigate('/');
  };

  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/bidding', icon: Calculator, label: 'Bidding' },
    { path: '/open-bids', icon: History, label: 'Open Bids' },
    { path: '/projects', icon: Briefcase, label: 'Active Projects' },
    { path: '/accounts-receivable', icon: DollarSign, label: 'Accounts Receivable' },
    { path: '/completed', icon: CheckCircle, label: 'Completed Projects' },
    { path: '/contractors', icon: Award, label: 'Contractors' },
    { path: '/map', icon: Map, label: 'Map' },
    { path: '/analytics', icon: BarChart3, label: 'Analytics' },
  ];

  // Add admin-only items
  if (isAdmin) {
    navItems.push({ path: '/admin', icon: Settings, label: 'Admin' });
    navItems.push({ path: '/users', icon: Users, label: 'Users' });
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="fixed left-0 top-0 h-screen w-64 bg-[#0f172a] border-r border-[rgba(255,255,255,0.1)] z-50 flex flex-col">
      <div className="p-6">
        <h1 className="text-2xl font-black tracking-tighter mb-1">
          <span className="text-[#f97316]">Mill</span>Metrix
        </h1>
        <p className="text-xs text-[#94a3b8] uppercase tracking-widest">
          Business Intelligence
        </p>
        
        {/* Division Selector */}
        <div className="mt-4">
          <label className="text-xs font-bold uppercase tracking-widest text-[#94a3b8] block mb-2">
            Division
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#f97316] pointer-events-none" />
            <select
              value={division}
              onChange={handleDivisionChange}
              className="w-full bg-[#1e293b] text-white pl-10 pr-4 py-2.5 rounded-lg border border-[rgba(255,255,255,0.1)] hover:border-[#f97316] focus:border-[#f97316] focus:outline-none transition-all appearance-none cursor-pointer font-semibold"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23f97316' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '12px'
              }}
            >
              <option value="texas" className="bg-[#1e293b] text-white">Texas</option>
              <option value="louisiana" className="bg-[#1e293b] text-white">Louisiana</option>
            </select>
          </div>
        </div>
      </div>

      <nav className="px-3 mt-6 flex-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 mb-2 rounded-lg transition-all ${isActive
                  ? 'bg-[#f97316] text-white'
                  : 'text-[#94a3b8] hover:bg-[#1e293b] hover:text-white'
                }`
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="p-6 space-y-4">
        {/* User Info */}
        <div className="bg-[#1e293b] p-4 rounded-lg border border-[rgba(255,255,255,0.1)]">
          <p className="text-xs text-[#94a3b8] mb-1">Logged in as</p>
          <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
          <p className="text-xs text-[#94a3b8] truncate">{user?.email}</p>
          {isAdmin && (
            <span className="inline-block mt-2 text-xs bg-[#f97316] text-white px-2 py-1 rounded uppercase font-bold">
              Admin
            </span>
          )}
        </div>

        {/* Profile Button */}
        <Button
          onClick={() => navigate('/profile')}
          className="w-full bg-[#1e293b] hover:bg-[#f97316] text-[#94a3b8] hover:text-white border border-[rgba(255,255,255,0.1)] transition-all"
        >
          <User className="w-4 h-4 mr-2" />
          Profile
        </Button>

        {/* Logout Button */}
        <Button
          onClick={handleLogout}
          className="w-full bg-[#1e293b] hover:bg-[#ef4444] text-[#94a3b8] hover:text-white border border-[rgba(255,255,255,0.1)] transition-all"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;
